<template>
  <div class="my-subs">

    <div class="subs-head">
      <div>
        <h3 class="subs-title">
          <v-icon color="#2d5a3d">notifications</v-icon>
          Mes notifications par univers
        </h3>
        <p class="subs-intro">
          Par défaut, chaque univers suit votre réglage global. Vous pouvez décider
          univers par univers, tous clients confondus.
        </p>
      </div>

      <div class="subs-global">
        <span class="subs-global-item">
          <v-icon small :color="globalSms ? '#2d5a3d' : '#b0b8b4'">sms</v-icon>
          SMS global : <strong>{{ globalSms ? 'activé' : 'désactivé' }}</strong>
        </span>
        <span class="subs-global-item">
          <v-icon small :color="globalEmail ? '#2d5a3d' : '#b0b8b4'">mail</v-icon>
          Email global : <strong>{{ globalEmail ? 'activé' : 'désactivé' }}</strong>
        </span>
      </div>
    </div>

    <v-alert
        v-if="globalSms && !phone"
        dense
        text
        type="warning"
        class="subs-alert"
    >
      Aucun numéro de téléphone n'est renseigné sur votre compte : aucun SMS ne
      pourra vous être envoyé, quel que soit le réglage ci-dessous.
    </v-alert>

    <div v-if="loading" class="subs-empty">Chargement…</div>

    <div v-else-if="!sites.length" class="subs-empty">
      Vous n'êtes rattaché à aucun univers.
    </div>

    <template v-else>
      <div
          v-for="group in groupedSites"
          :key="group.client_id"
          class="subs-client"
      >
        <div class="subs-client-head">
          <v-icon small color="#8a9a90">business</v-icon>
          <span>{{ group.client_name || 'Sans client' }}</span>
          <span class="subs-client-count">{{ group.sites.length }}</span>
        </div>

        <table class="subs-table">
          <thead>
          <tr>
            <th>Univers</th>
            <th class="center">SMS</th>
            <th class="center">Email</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="site in group.sites" :key="site.id">
            <td>{{ site.name }}</td>

            <td class="center">
              <div class="subs-segmented">
                <button
                    v-for="opt in tristate"
                    :key="'s' + site.id + String(opt.value)"
                    type="button"
                    :class="['seg-btn', { active: draft[site.id].optin_sms === opt.value }]"
                    @click="draft[site.id].optin_sms = opt.value"
                >{{ opt.label }}</button>
              </div>
              <span class="subs-effective">{{ effectiveLabel(site.id, 'sms') }}</span>
            </td>

            <td class="center">
              <div class="subs-segmented">
                <button
                    v-for="opt in tristate"
                    :key="'e' + site.id + String(opt.value)"
                    type="button"
                    :class="['seg-btn', { active: draft[site.id].optin_mail === opt.value }]"
                    @click="draft[site.id].optin_mail = opt.value"
                >{{ opt.label }}</button>
              </div>
              <span class="subs-effective">{{ effectiveLabel(site.id, 'email') }}</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="subs-actions">
        <span class="subs-dirty" v-if="dirty">Modifications non enregistrées</span>
        <v-btn color="#2d5a3d" dark :disabled="!dirty" :loading="saving" @click="save">
          <v-icon left small>save</v-icon>
          Enregistrer
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "MyNotificationSubscriptions",

  data() {
    return {
      loading: false,
      saving: false,

      globalSms: false,
      globalEmail: true,
      phone: null,

      sites: [],
      draft: {},
      initialDraft: "",

      tristate: [
        {value: null, label: "Hérité"},
        {value: 1, label: "Oui"},
        {value: 0, label: "Non"},
      ],
    };
  },

  computed: {
    groupedSites() {
      const map = {};

      for (const site of this.sites) {
        const key = site.client_id || 0;

        if (!map[key]) {
          map[key] = {
            client_id: key,
            client_name: site.client_name,
            sites: [],
          };
        }

        map[key].sites.push(site);
      }

      return Object.values(map).sort((a, b) =>
          (a.client_name || "").localeCompare(b.client_name || "")
      );
    },

    dirty() {
      return JSON.stringify(this.draft) !== this.initialDraft;
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;

      try {
        const data = await this.$store.dispatch("alertPreferencesMineRequest");

        this.globalSms = !!data.global_optin_sms;
        this.globalEmail = !!data.global_optin_mail;
        this.phone = data.phone;
        this.sites = data.sites || [];

        const draft = {};

        for (const site of this.sites) {
          draft[site.id] = {
            optin_sms: site.optin_sms === null || site.optin_sms === undefined ? null : Number(site.optin_sms),
            optin_mail: site.optin_mail === null || site.optin_mail === undefined ? null : Number(site.optin_mail),
          };
        }

        this.draft = draft;
        this.initialDraft = JSON.stringify(draft);
      } catch (err) {
        this.showErr(err);
      } finally {
        this.loading = false;
      }
    },

    /** Ce qui s'applique réellement, une fois l'héritage résolu. */
    effectiveLabel(siteId, channel) {
      const entry = this.draft[siteId];
      if (!entry) return "";

      const value = channel === "sms" ? entry.optin_sms : entry.optin_mail;

      if (value === null) {
        const global = channel === "sms" ? this.globalSms : this.globalEmail;
        return global ? "reçoit" : "ne reçoit pas";
      }

      return value === 1 ? "reçoit" : "ne reçoit pas";
    },

    async save() {
      this.saving = true;

      try {
        const payload = this.sites.map((site) => ({
          site_id: site.id,
          optin_sms: this.draft[site.id].optin_sms,
          optin_mail: this.draft[site.id].optin_mail,
        }));

        await this.$store.dispatch("alertPreferencesMineSaveRequest", {sites: payload});

        this.initialDraft = JSON.stringify(this.draft);

        this.$store.commit("setSnack", {
          type: "green",
          err: "Préférences de notification enregistrées.",
        });
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    showErr(err) {
      const message =
          (err && err.response && err.response.data) || "Une erreur est survenue";

      this.$store.commit("setSnack", {
        type: "red",
        err: typeof message === "string" ? message : JSON.stringify(message),
      });
    },
  },
};
</script>

<style scoped>
.my-subs {
  width: 100%;
}

.subs-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.subs-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2d24;
  margin: 0;
}

.subs-intro {
  font-size: 0.82rem;
  color: #6b7a70;
  margin: 6px 0 0;
  max-width: 620px;
}

.subs-global {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: #6b7a70;
}

.subs-global-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.subs-alert {
  font-size: 0.82rem;
}

.subs-empty {
  font-size: 0.85rem;
  color: #8a9a90;
  font-style: italic;
  padding: 16px 0;
}

.subs-client {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 14px;
}

.subs-client-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f6f8f7;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2d24;
}

.subs-client-count {
  margin-left: auto;
  font-size: 0.7rem;
  color: #8a9a90;
  font-weight: 500;
}

.subs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.subs-table th {
  text-align: left;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 6px 12px;
  border-bottom: 1px solid #e6ebe8;
}

.subs-table th.center,
.subs-table td.center {
  text-align: center;
  width: 210px;
}

.subs-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f4f6f5;
  color: #1f2d24;
}

.subs-table tr:last-child td {
  border-bottom: none;
}

.subs-segmented {
  display: inline-flex;
  border: 1px solid #d8e0db;
  border-radius: 8px;
  overflow: hidden;
}

.seg-btn {
  background: #fff;
  border: none;
  padding: 4px 12px;
  font-size: 0.74rem;
  color: #6b7a70;
  cursor: pointer;
  transition: all 0.15s;
}

.seg-btn + .seg-btn {
  border-left: 1px solid #d8e0db;
}

.seg-btn.active {
  background: #2d5a3d;
  color: #fff;
  font-weight: 600;
}

.subs-effective {
  display: block;
  font-size: 0.68rem;
  color: #a0aca5;
  margin-top: 3px;
}

.subs-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.subs-dirty {
  font-size: 0.76rem;
  color: #c9822a;
}
</style>
